"use client";

import { useState, type ReactNode } from "react";

import { ImageLightbox } from "@/components/ui/image-lightbox";
import { cn } from "@/lib/utils";

export type MediaMosaicItem = {
  key: string;
  src: string;
  alt?: string;
  kind: "image" | "video";
};

export function MediaMosaic({
  items,
  overlay,
  className,
}: {
  items: MediaMosaicItem[];
  overlay?: (item: MediaMosaicItem, index: number) => ReactNode;
  className?: string;
}) {
  const count = items.length;
  const images = items.filter((item) => item.kind === "image");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  if (count === 0) {
    return null;
  }

  return (
    <>
      <div
        className={cn(
          "w-full min-w-0 overflow-hidden rounded-lg",
          count === 1 && "block",
          count === 2 && "grid grid-cols-2 gap-1",
          count === 3 && "grid h-[22rem] grid-cols-2 grid-rows-2 gap-1",
          count >= 4 && "grid grid-cols-2 gap-1",
          className,
        )}
      >
        {items.map((item, index) => (
          <MosaicTile
            key={item.key}
            item={item}
            count={count}
            index={index}
            overlay={overlay?.(item, index)}
            onOpenImage={() => {
              const imageIndex = images.findIndex((image) => image.key === item.key);
              if (imageIndex >= 0) {
                setLightboxIndex(imageIndex);
              }
            }}
          />
        ))}
      </div>
      <ImageLightbox
        open={lightboxIndex !== null}
        images={images.map((item) => ({ src: item.src, alt: item.alt }))}
        index={lightboxIndex ?? 0}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxIndex(null);
          }
        }}
        onIndexChange={setLightboxIndex}
      />
    </>
  );
}

function MosaicTile({
  item,
  count,
  index,
  overlay,
  onOpenImage,
}: {
  item: MediaMosaicItem;
  count: number;
  index: number;
  overlay?: ReactNode;
  onOpenImage: () => void;
}) {
  const mediaClassName =
    count === 1
      ? "max-h-96 w-full max-w-full object-cover"
      : "h-full w-full max-w-full object-cover";
  const media =
    item.kind === "video" ? (
      <video src={item.src} controls className={cn(mediaClassName, "bg-black")} />
    ) : (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={item.src}
        alt={item.alt ?? "Csatolt kép"}
        className={mediaClassName}
      />
    );

  return (
    <div
      className={cn(
        "relative min-h-0 min-w-0 overflow-hidden bg-muted",
        count === 1 && "max-h-96",
        count === 2 && "aspect-[4/3]",
        count === 3 && "h-full",
        count === 3 && index === 0 && "row-span-2",
        count >= 4 && "aspect-square",
      )}
    >
      {item.kind === "image" ? (
        <button
          type="button"
          className="block h-full w-full cursor-zoom-in"
          aria-label={`${item.alt ?? "Kép"} megnyitása`}
          onClick={onOpenImage}
        >
          {media}
        </button>
      ) : (
        media
      )}
      {overlay}
    </div>
  );
}
